import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

/**
 * Lightweight toast notification system.
 *
 * Usage anywhere in the tree:
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Something broke');
 *   toast.info('Discovery started');
 *   toast.dismiss(id);  // optional — toasts auto-dismiss after 5s
 *
 * Stacks bottom-right. Each toast has its own auto-dismiss timer. Hovering
 * the stack pauses dismissal so users get time to read.
 */

const ToastContext = createContext(null);
let nextId = 1;

const DEFAULT_DURATION = 5000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [paused, setPaused] = useState(false);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((variant, message, opts = {}) => {
    const id = nextId++;
    const toast = {
      id,
      variant, // 'success' | 'error' | 'info' | 'warning'
      message,
      duration: opts.duration ?? DEFAULT_DURATION,
      action: opts.action || null, // { label, onClick }
      createdAt: Date.now(),
    };
    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  // Auto-dismiss timer for each toast. Pauses while user hovers the stack.
  useEffect(() => {
    if (paused || toasts.length === 0) return;
    const timers = toasts.map(t =>
      setTimeout(() => dismiss(t.id), Math.max(0, t.duration - (Date.now() - t.createdAt)))
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts, paused, dismiss]);

  const api = {
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error',   msg, opts),
    info:    (msg, opts) => push('info',    msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="toast-stack"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }) {
  const ICONS = { success: '✓', error: '✕', info: 'ℹ', warning: '⚠' };
  return (
    <div className={`toast toast-${toast.variant}`}>
      <div className="toast-icon">{ICONS[toast.variant] || 'ℹ'}</div>
      <div className="toast-message">{toast.message}</div>
      {toast.action && (
        <button className="toast-action" onClick={() => { toast.action.onClick(); onDismiss(); }}>
          {toast.action.label}
        </button>
      )}
      <button className="toast-close" onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Graceful no-op if Provider isn't mounted (e.g. unit tests).
    return { success: () => {}, error: () => {}, info: () => {}, warning: () => {}, dismiss: () => {} };
  }
  return ctx;
}